/**
 * User Menu component
 * Displays user avatar, dropdown menu with account actions, workspace switcher
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../lib/auth';
import Avatar from '../common/Avatar';
import { Dropdown, DropdownItem, DropdownLabel, DropdownSeparator } from '../common/Dropdown';
import WorkspaceSwitcher from '../common/WorkspaceSwitcher';
import DomainSwitcher from '../common/DomainSwitcher';
import CreateWorkspaceModal from '../common/CreateWorkspaceModal';
import AddWebsiteModal from '../common/AddWebsiteModal';
import apiClient from '../../lib/api';

interface Workspace {
  id: string;
  name: string;
  slug: string;
  role: string;
  isActive: boolean;
}

export default function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const { user, workspace, logout, setWorkspace: _setWorkspace, setActiveWebsite } = useAuthStore();

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddWebsiteModal, setShowAddWebsiteModal] = useState(false);

  // Get wallet balance from auth store (updated by BillingSettingsPage)
  const walletBalance = workspace?.walletBalance ?? null;

  // Fetch workspaces on mount and when URL workspace changes or workspace name updates
  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        setIsLoadingWorkspaces(true);
        const response = await apiClient.get('/auth/workspaces');
        const workspacesList = response.data.workspaces || [];

        // Override isActive based on URL slug (URL is source of truth)
        const workspacesWithCorrectActive = workspacesList.map((ws: Workspace) => ({
          ...ws,
          isActive: ws.slug === workspaceSlug,
        }));

        setWorkspaces(workspacesWithCorrectActive);
      } catch (error) {
        console.error('Failed to fetch workspaces:', error);
      } finally {
        setIsLoadingWorkspaces(false);
      }
    };

    fetchWorkspaces();
  }, [workspaceSlug, workspace?.name]); // Re-fetch when workspace name changes

  const handleSwitchWorkspace = async (workspaceId: string) => {
    try {
      // Find the workspace in the list to get its slug
      const targetWorkspace = workspaces.find(ws => ws.id === workspaceId);
      if (!targetWorkspace) {
        console.error('[WORKSPACE] Workspace not found in list:', workspaceId);
        return;
      }

      console.log('[WORKSPACE] Switching to workspace:', targetWorkspace.name, targetWorkspace.slug);

      // Get current path and replace workspace slug to maintain relative page
      const currentPath = window.location.pathname;
      const pathAfterWorkspace = currentPath.substring(currentPath.indexOf('/', 1)); // Get path after first /workspaceSlug

      // If we can't determine the path after workspace, default to dashboard
      const targetPath = pathAfterWorkspace && pathAfterWorkspace !== '/'
        ? `/${targetWorkspace.slug}${pathAfterWorkspace}`
        : `/${targetWorkspace.slug}/dashboard`;

      console.log('[WORKSPACE] Navigating from', currentPath, 'to', targetPath);

      // Navigate to the same page but in the new workspace
      navigate(targetPath);

      // Reload page to refresh all workspace data (including websites)
      // Small delay to let navigation complete first
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error: any) {
      console.error('Failed to switch workspace:', error);
      alert(error.response?.data?.message || t('userMenu.workspaces.errors.switchFailed', 'Failed to switch workspace'));
    }
  };

  const handleCreateWorkspace = async (name: string) => {
    try {
      const response = await apiClient.post('/auth/workspaces', { name });

      // Switch to new workspace (navigate will trigger workspace refetch)
      if (response.data.workspace) {
        const newWorkspace = response.data.workspace;
        console.log('[WORKSPACE] Created new workspace:', newWorkspace.name, newWorkspace.slug);
        navigate(`/${newWorkspace.slug}/dashboard`);
      }
    } catch (error: any) {
      console.error('Failed to create workspace:', error);
      throw new Error(error.response?.data?.message || t('userMenu.createWorkspace.errors.failed', 'Failed to create workspace'));
    }
  };

  const handleSwitchDomain = async (websiteId: string) => {
    try {
      console.log('[DOMAIN] Switching to website:', websiteId);
      await setActiveWebsite(websiteId);

      // Reload the page to refresh all data with new website context
      window.location.reload();
    } catch (error: any) {
      console.error('Failed to switch website:', error);
      alert(error.response?.data?.message || t('domainSwitcher.errors.switchFailed', 'Failed to switch website'));
    }
  };

  const handleAddWebsite = () => {
    console.log('[DOMAIN] Add website clicked - show modal');
    setShowAddWebsiteModal(true);
  };

  const handleCreateWebsite = async (websiteData: {
    domain: string;
    name: string;
    description?: string;
  }) => {
    try {
      // Check for duplicate domain (frontend validation)
      const normalizedDomain = websiteData.domain.toLowerCase().trim()
        .replace(/^https?:\/\//, '') // Remove protocol if present
        .replace(/^www\./, '');      // Remove www. for comparison

      const existingWebsite = workspace?.websites?.find(w => {
        const existingDomain = w.domain.toLowerCase()
          .replace(/^https?:\/\//, '') // Remove protocol for comparison
          .replace(/^www\./, '');      // Remove www. for comparison
        return existingDomain === normalizedDomain;
      });

      if (existingWebsite) {
        throw new Error(t('addWebsite.errors.duplicate', `Website ${websiteData.domain} already exists in this workspace`));
      }

      console.log('[DOMAIN] Creating website:', websiteData);
      const response = await apiClient.post('/websites', websiteData);

      console.log('[DOMAIN] Response:', response.data);

      // Backend returns { success: true, data: { website, brandExtractionJobId } }
      const newWebsite = response.data.data?.website || response.data.website;

      if (newWebsite) {
        console.log('[DOMAIN] Website created successfully:', newWebsite);

        // Fetch fresh workspace data from API to ensure we have the latest state
        const meResponse = await apiClient.get('/auth/me');
        console.log('[DOMAIN] Fetched workspace:', meResponse.data);

        if (meResponse.data.workspace) {
          console.log('[DOMAIN] Setting fresh workspace with websites:', meResponse.data.workspace.websites);
          _setWorkspace(meResponse.data.workspace);
        }

        // Success feedback will be shown by the modal before closing
      }
    } catch (error: any) {
      console.error('Failed to create website:', error);
      throw new Error(error.response?.data?.message || t('addWebsite.errors.failed', 'Failed to add website'));
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Dropdown
        trigger={
          <button
            className="cursor-pointer transition-opacity hover:opacity-80 outline-none focus:outline-none"
            aria-label="User menu"
          >
            <Avatar fullName={user.fullName || user.email} size={40} />
          </button>
        }
      >
        {/* User info */}
        <div className="px-3 py-2 border-b-2 border-gray-200 dark:border-gray-700">
          <div className="font-bold text-sm dark:text-white">{user.fullName || user.email}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
          {workspace && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {workspace.name}
            </div>
          )}
        </div>

        {/* Workspace switcher */}
        <WorkspaceSwitcher
          workspaces={workspaces}
          onSwitchWorkspace={handleSwitchWorkspace}
          onCreateWorkspace={() => setShowCreateModal(true)}
          isLoading={isLoadingWorkspaces}
        />

        <DropdownSeparator />

        {/* Domain/Website switcher */}
        <DomainSwitcher
          websites={workspace?.websites || []}
          onSwitchDomain={handleSwitchDomain}
          onAddWebsite={handleAddWebsite}
        />

        <DropdownSeparator />

        {/* Settings Menu */}
        <DropdownLabel>{t('userMenu.settings.label', 'Settings')}</DropdownLabel>

        <DropdownItem onSelect={() => navigate(`/${workspaceSlug}/settings`)}>
          {t('userMenu.settings.account', 'Account')}
        </DropdownItem>

        <DropdownItem onSelect={() => navigate(`/${workspaceSlug}/settings/knowledge`)}>
          {t('userMenu.settings.knowledge', 'Knowledge Taxonomy')}
        </DropdownItem>

        <DropdownItem onSelect={() => navigate(`/${workspaceSlug}/settings/brand`)}>
          {t('userMenu.settings.brand', 'Visual Brand Guide')}
        </DropdownItem>

        <DropdownItem onSelect={() => navigate(`/${workspaceSlug}/settings/content`)}>
          {t('userMenu.settings.content', 'Preferred Content')}
        </DropdownItem>

        <DropdownItem onSelect={() => navigate(`/${workspaceSlug}/settings/api`)}>
          {t('userMenu.settings.api', 'API Connections')}
        </DropdownItem>

        <DropdownItem onSelect={() => navigate(`/${workspaceSlug}/settings/company`)}>
          {t('userMenu.settings.company', 'Company Settings')}
        </DropdownItem>

        {/* Workspace Settings - Only for Owner */}
        {workspace && workspace.role === 'owner' && (
          <DropdownItem onSelect={() => navigate(`/${workspaceSlug}/settings/workspace`)}>
            {t('userMenu.settings.workspace', 'Workspace Settings')}
          </DropdownItem>
        )}

        <DropdownItem onSelect={() => navigate(`/${workspaceSlug}/settings/billing`)}>
          <div className="flex items-center justify-between w-full">
            <span>{t('userMenu.settings.billing', 'Billing')}</span>
            {walletBalance !== null && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {walletBalance.toLocaleString()} {t('common.tokens', 'tokens')}
              </span>
            )}
          </div>
        </DropdownItem>

        <DropdownSeparator />

        <DropdownItem onSelect={handleLogout} destructive>
          {t('userMenu.account.logout', 'Logout')}
        </DropdownItem>
      </Dropdown>

      {/* Create workspace modal */}
      <CreateWorkspaceModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateWorkspace}
      />

      {/* Add website modal */}
      <AddWebsiteModal
        open={showAddWebsiteModal}
        onClose={() => setShowAddWebsiteModal(false)}
        onCreate={handleCreateWebsite}
      />
    </>
  );
}
